import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { ALLOWED_DOMAIN, authConfigured } from "./authConfig";
import { fetchDashboard, uploadCsv } from "./api";
import LoginScreen from "./components/LoginScreen";
import Dashboard from "./components/Dashboard";
import BeginningOfMonth from "./components/BeginningOfMonth";
import Deltas from "./components/Deltas";
import BudgetForecast from "./components/BudgetForecast";
import ShiftsHours from "./components/ShiftsHours";
import YearToDate from "./components/YearToDate";
import Weekly from "./components/Weekly";
import PnL from "./components/PnL";
import Executive from "./components/Executive";
import Overtime from "./components/Overtime";
import MonthlyReports from "./components/MonthlyReports";
import "./App.css";

const TABS = [
  { id: "dashboard", label: "Dashboard" },
  { id: "deltas", label: "Deltas" },
  { id: "budget", label: "Budget vs Forecast" },
  { id: "shifts", label: "Shifts & Hours" },
  { id: "ytd", label: "Year to Date" },
  { id: "weekly", label: "Weekly" },
  { id: "pnl", label: "P&L" },
  { id: "bom", label: "Beginning of Month" },
  { id: "overtime", label: "Overtime" },
  { id: "executive", label: "Executive", lock: true },
  { id: "reports", label: "Monthly Reports" },
];

export default function App() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = accounts[0];

  const email = (account?.username || account?.idTokenClaims?.email || "").toLowerCase();
  const domainOk = !authConfigured || (email && email.endsWith(`@${ALLOWED_DOMAIN}`));
  const canView = authConfigured ? isAuthenticated && domainOk : true;

  const [tab, setTab] = useState("dashboard");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  // Clients in the upload that reached no division and so contribute to no total.
  const [unclassified, setUnclassified] = useState(null);
  const fileRef = useRef(null);

  const ctx = { msalInstance: instance, account };

  const load = useCallback(async (month) => {
    setLoading(true); setError(null);
    try { setData(await fetchDashboard(ctx, month)); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  useEffect(() => { if (canView) load(); }, [canView, load]);

  const onPickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    setUploading(true); setUploadMsg(null); setUnclassified(null); setError(null);
    try {
      const text = await file.text();
      const res = await uploadCsv(ctx, text, file.name);
      setUploadMsg(`Loaded ${res.months?.join(", ")} · $${res.revenue.toLocaleString()} (${res.rowCount} shifts)`);
      setUnclassified(res.unclassified?.length ? res.unclassified : null);
      await load(); // reload with the newly uploaded data
    } catch (e2) {
      setError(e2.message);
    } finally {
      setUploading(false);
    }
  };

  const onMonth = (e) => load(e.target.value);
  const signOut = () => instance.logoutRedirect();

  if (!canView) {
    if (authConfigured && isAuthenticated && !domainOk) {
      return (
        <div className="denied-shell">
          <div className="denied-card">
            <h1>Access restricted</h1>
            <p>This dashboard is limited to <strong>@{ALLOWED_DOMAIN}</strong> accounts.</p>
            <p className="denied-email">Signed in as {email || "unknown"}</p>
            <button className="btn" onClick={signOut}>Sign out</button>
          </div>
        </div>
      );
    }
    return <LoginScreen />;
  }

  const empty = data?.empty;

  const renderTab = () => {
    if (tab === "executive") return <Executive ctx={ctx} />;
    if (tab === "overtime") return <Overtime ctx={ctx} />;
    if (tab === "reports") return <MonthlyReports data={data} ctx={ctx} />;
    if (!data || empty) return null;
    switch (tab) {
      case "bom": return <BeginningOfMonth data={data} />;
      case "deltas": return <Deltas data={data} />;
      case "budget": return <BudgetForecast data={data} />;
      case "shifts": return <ShiftsHours data={data} />;
      case "ytd": return <YearToDate data={data} ctx={ctx} />;
      case "weekly": return <Weekly data={data} ctx={ctx} />;
      case "pnl": return <PnL data={data} ctx={ctx} />;
      default: return <Dashboard data={data} />;
    }
  };

  return (
    <div className="app-shell">
      <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} style={{ display: "none" }} />
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark">◆</span>
          <div>
            <div className="brand-title">Revenue &amp; Profitability</div>
            <div className="brand-sub">NursePartners Inc.</div>
          </div>
        </div>
        <div className="topbar-right">
          {data?.months?.length > 1 && (
            <select className="month-select" value={data.month} onChange={onMonth}>
              {data.months.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          )}
          {data?.monthLabel && !empty && <span className="month-chip">{data.monthLabel}</span>}
          <button className="btn btn-refresh" onClick={onPickFile} disabled={uploading}>
            {uploading ? "Uploading…" : "Upload CSV"}
          </button>
          {authConfigured && <button className="btn btn-ghost" onClick={signOut}>Sign out</button>}
        </div>
      </header>

      {(tab === "executive" || tab === "reports" || !empty) && (
        <nav className="tabbar">
          {TABS.map((t) => (
            <button key={t.id} className={"tab" + (tab === t.id ? " active" : "")} onClick={() => setTab(t.id)}>
              {t.lock && <span className="tab-lock">🔒</span>}{t.label}
            </button>
          ))}
        </nav>
      )}

      <main className="content">
        {loading && !data && <div className="state">Loading…</div>}
        {error && <div className="state error">Error: {error}</div>}
        {uploadMsg && <div className="upload-msg">✓ {uploadMsg}</div>}
        {unclassified && (
          <div className="upload-warn">
            <b>{unclassified.length} client{unclassified.length > 1 ? "s" : ""} not counted</b>
            {" — no division in Teambridge, so their shifts are excluded from every total. "}
            {"Set Client Type on the Clients record, then re-upload."}
            <ul>
              {unclassified.map((u) => (
                <li key={u.client}>
                  {u.client} · {u.rows} shift{u.rows > 1 ? "s" : ""}
                  {u.revenue > 0 ? ` · $${Math.round(u.revenue).toLocaleString()}` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
        {empty && tab !== "executive" && tab !== "reports" ? (
          <div className="empty-state">
            <div className="empty-icon">📄</div>
            <h2>No data yet</h2>
            <p>{data.message}</p>
            <button className="btn btn-refresh" onClick={onPickFile} disabled={uploading}>
              {uploading ? "Uploading…" : "Upload Teambridge CSV"}
            </button>
            <p className="empty-hint">Export "All Shifts" for the month from Teambridge and upload the CSV here.</p>
          </div>
        ) : renderTab()}
      </main>

      <footer className="footer">
        {data && !empty && (
          <>Source: {data.source} · Last updated {data.lastUpdated ? new Date(data.lastUpdated).toLocaleString() : "—"}
          {data.filename ? ` · ${data.filename}` : ""} · {data.rowCount} shifts</>
        )}
      </footer>
    </div>
  );
}
